import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Pricing", href: "#pricing" },
  { label: "Work", href: "#work" },
  { label: "Process", href: "#process" },
  { label: "FAQ", href: "#faq" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-[999] transition-colors duration-500 ${
          scrolled ? "backdrop-blur-xl bg-cream/70 border-b border-charcoal/5" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <a
            href="#top"
            onClick={(e) => { e.preventDefault(); go("#top"); }}
            className="font-heading text-xl md:text-2xl tracking-tight text-charcoal"
            data-testid="nav-logo"
          >
            Ankita Gogia<span className="text-burgundy">.</span>
          </a>

          <div className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="relative text-sm text-charcoal/70 hover:text-charcoal transition-colors duration-300 group"
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-burgundy group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </div>

          <button
            onClick={() => go("#contact")}
            className="hidden md:inline-flex rounded-full bg-charcoal text-cream hover:bg-burgundy px-6 py-3 text-sm font-medium tracking-wide transition-colors duration-300"
            data-testid="nav-cta"
          >
            Let's Talk
          </button>

          <button
            className="md:hidden text-charcoal"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed inset-x-0 top-20 z-[998] md:hidden backdrop-blur-xl bg-cream/95 border-b border-charcoal/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left font-heading text-2xl text-charcoal"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#contact")}
                className="mt-2 rounded-full bg-charcoal text-cream px-6 py-4 text-sm font-medium"
              >
                Let's Talk
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
